import React, { Suspense, useCallback, useRef } from 'react';
import { atom, useAtom } from 'jotai';
import { useRevalidator } from 'react-router-dom';
import { Button, ButtonGroup } from '@nike/eds';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { useQuery } from '@tanstack/react-query';
import { auditFetch } from '../../clientAPI/identity';
import { QUERY_KEYS } from '../../constants';
import Select from 'react-select';
import { number } from 'prop-types';
import dayjs from 'dayjs';

type AuditLogResponse = {
  _id: string;
  moduleName: string;
  operator: string;
  operationTime: number;
  operationType: string;
  operationResult: number;
  event: string;
  newValue: string;
  oldValue: string;
  remark: string;
};

const listUserQueryAtom = atom<{
  pageSize?: string;
  currentPage?: string;
  eventKey?: string;
  operator?: string;
  moduleName?: string;
}>({ pageSize: '15' });

function useUserList() {
  const revalidator = useRevalidator();
  const [listUserQuery] = useAtom(listUserQueryAtom);
  const qs = objectToQueryString(removeNullishValue(listUserQuery)).toString();
  return useQuery([QUERY_KEYS.AUDIT_LIST, qs], auditFetch(), {
    staleTime: Infinity,
    suspense: true,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });
}

function AuditLogShell({ children }: { children: React.ReactNode }) {
  const options = [
    {
      value: 'Dictionaries Nike Customer',
      label: 'Dictionaries Nike Customer',
    },
    { value: 'Dictionaries Stop Words', label: 'Dictionaries Stop Words' },
    { value: 'Dictionaries NO GO', label: 'Dictionaries NO GO' },
    { value: 'Rewrite', label: 'Rewrite' },
    { value: 'Nickname', label: 'Nickname' },
    { value: 'User Management', label: 'User Management' },
  ];

  const eventKeyInputRef = useRef<HTMLInputElement>(null);
  // const eventKeyValueRef = useRef<string>('');

  const operatorInputRef = useRef<HTMLInputElement>(null);
  // const operatorValueRef = useRef<string>('');

  const [, setListUserQuery] = useAtom(listUserQueryAtom);

  const onSearch = (option: any) => {
    const eventKey = eventKeyInputRef?.current?.value.trim() || '';
    const operator = operatorInputRef?.current?.value.trim() || '';
    const moduleName = option?.value || '';
    setListUserQuery(() => {
      return { eventKey, operator, moduleName };
    });
  };
  return (
    <div className='tw-bg-white'>
      <div className='tw-max-w-6xl tw-mx-auto tw-p-10'>
        <p className='eds-type--display-5 tw-mb-10'>Audit Log</p>
        <div className='tw-flex tw-justify-between tw-my-6 tw-items-center'>
          <div className='tw-flex'>
            <Input
              placeholder='Event String'
              className='tw-border-b tw-w-52 tw-mr-10'
              ref={eventKeyInputRef}
              onChange={onSearch}
            />
            <Input
              placeholder='User Email'
              className='tw-border-b tw-w-52 tw-mr-10'
              ref={operatorInputRef}
              onChange={onSearch}
            />

            <Select
              options={options}
              isClearable
              className='tw-w-80 tw-mr-6'
              placeholder='Module Name'
              onChange={onSearch}
            />
          </div>
        </div>
        <Suspense fallback={<AuditListSkeleton />}>{children}</Suspense>
        <Suspense fallback={null}>
          <AuditListPagination></AuditListPagination>
        </Suspense>
      </div>
    </div>
  );
}

function AuditLogCoreList() {
  const { data } = useUserList();
  return (
    <div className='tw-container tw-mx-auto tw-text-sm'>
      <div className='tw-flex tw-flex-col tw-overflow-x-auto tw-text-sm '>
        <div className='tw-flex tw-text-left'>
          <div className='tw-w-52 tw-px-2 tw-py-3 sm:tw-p-3 tw-bg-[#e1e6eb] tw-mr-0.5'>
            Time
          </div>
          <div className='tw-flex-1 tw-px-2 tw-py-3 sm:tw-p-3 tw-bg-[#e1e6eb]'>
            Event
          </div>
        </div>
      </div>

      {data.auditList?.length === 0 && (
        <h2 className='eds-type--display-5 tw-py-32 tw-text-center'>
          No Data available
        </h2>
      )}

      {data.auditList.map((audit: AuditLogResponse, index: number) => {
        return (
          <div
            key={audit._id}
            className='tw-flex tw-flex-col tw-overflow-x-auto tw-text-sm'
          >
            <div
              className={`tw-flex tw-text-left tw-bg-${
                index % 2 === 0 ? 'white' : 'gray-100'
              }`}
            >
              <div className='tw-w-52 tw-px-2 tw-py-3 sm:tw-p-3 tw-mr-0.5'>
                {toLocalDate(audit.operationTime)}
              </div>
              <div className='tw-flex-1 tw-px-2 tw-px-3 tw-py-2.5'>
                <p className='tw-break-all'>
                  user=&quot;{audit.operator}&quot;&nbsp; moduleName=&quot;
                  {audit.moduleName}&quot;&nbsp; operationType=&quot;
                  {audit.operationType}&quot;&nbsp; event=&quot;{audit.event}
                  &quot; newValue={audit.newValue || 'null'}&nbsp; oldValue=
                  {audit.oldValue || 'null'}&nbsp; remark=
                  {audit.remark || 'null'}&nbsp; operationResult=
                  {audit.operationResult === 1 ? 'success' : 'failed'}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AuditListPagination() {
  const { data } = useUserList();
  const page = data.currentPage;
  const hasMoreData = data.totalPages > page;
  const noPagination = page === 1 && !hasMoreData;
  const [, setListUserQuery] = useAtom(listUserQueryAtom);
  const setPage = useCallback(
    (page: number) => {
      const currentPage = '' + page;
      setListUserQuery((prev) => {
        return { ...prev, currentPage };
      });
    },
    [setListUserQuery],
  );
  return (
    <div className='tw-flex tw-items-center tw-justify-between tw-pb-40'>
      {!noPagination && (
        <p className='tw-text-sm tw-font-bold'>Current Page: {page}</p>
      )}
      <ButtonGroup>
        {page !== 1 && (
          <Button size='small' onClick={() => setPage(page - 1)}>
            Previous Page
          </Button>
        )}
        {hasMoreData && (
          <Button size='small' onClick={() => setPage(page + 1)}>
            Next Page
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
}

function toLocalDate(mill: number) {
  return dayjs(mill).format('YYYY-MM-DD HH:mm:ss');
}

function AuditListSkeleton() {
  return (
    <div className='tw-container tw-mx-auto tw-text-sm'>
      <div className='tw-flex tw-flex-col tw-overflow-x-auto tw-text-sm'>
        <div className='tw-flex tw-text-left tw-mb-20'>
          <div className='tw-w-52 tw-px-2 tw-py-3 sm:tw-p-3 tw-bg-[#e1e6eb] tw-mr-0.5'>
            Time
          </div>
          <div className='tw-flex-1 tw-px-2 tw-py-3 sm:tw-p-3 tw-bg-[#e1e6eb]'>
            Event
          </div>
        </div>
        <Loader />
      </div>
    </div>
  );
}

// components

export function AuditLog() {
  return (
    <AuditLogShell>
      <AuditLogCoreList></AuditLogCoreList>
    </AuditLogShell>
  );
}

function removeNullishValue(o: Record<string, string | null | undefined>) {
  const copy = { ...o };
  for (const key in copy) {
    if (copy[key] === '' || copy[key] === null || copy[key] === undefined) {
      delete copy[key];
    }
  }
  return copy as Record<string, string>;
}

function objectToQueryString(obj: Record<string, string>): string {
  return Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&');
}
